<template>
  <slot
    :id="controlId"
    :describedby="describedBy"
    :invalid="invalid"
  />
</template>

<script setup lang="ts">
import { computed, useId } from 'vue'
import { useFormField } from '@nuxt/ui/composables/useFormField'

// Bridges a non-Nuxt-UI control into UFormField's id/aria-invalid/aria-describedby wiring via useFormField().
// Must render inside a UFormField; on its own the composable finds no field and the slot gets nothing.
defineSlots<{
  /** The control, handed the ARIA wiring the surrounding field computed. */
  default?: (props: { id: string; describedby: string | undefined; invalid: boolean }) => unknown
}>()

const { ariaAttrs, id } = useFormField()

// Fallback covers rendering outside a UFormField; spares consumers an optional id under exactOptionalPropertyTypes.
const fallbackId = useId()
const controlId = computed(() => (typeof id.value === 'string' ? id.value : fallbackId))

// The vendor types this as Record<string, any>; narrow it once here so nothing
// downstream inherits the `any` (docs/standards/trust-boundaries.md).
const attributes = computed<Record<string, unknown>>(() => ariaAttrs.value ?? {})

const describedBy = computed(() => {
  const value = attributes.value['aria-describedby']
  return typeof value === 'string' ? value : undefined
})
const invalid = computed(() => attributes.value['aria-invalid'] === true)
</script>
